import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setShowMenu, setShowSettings, setSettingsTab, setPage, setShowShareCalendar, setProfile, incrementOperation, decrementOperation, setProfileLoading } from '../Redux/store';
import { X, Settings, User, Share2, RefreshCw, LocateFixed, Loader2 } from 'lucide-react';
import { reloadChats } from '../Loaders/ChatsLoader';
import { supabase } from '../supabase/client';

/** Context-sensitive dropdown menu — options change per page */
function MenuDropdown() {
  const open = useSelector((s) => s.ui.showMenu);
  const page = useSelector((s) => s.ui.currentPage);
  const authUserId = useSelector((s) => s.user.authUserId);
  const chatsLoading = useSelector((s) => s.chats.loading);
  const profile = useSelector((s) => s.user.profile);
  const profileLoading = useSelector((s) => s.user.profileLoading);
  const dispatch = useDispatch();
  const [options, setOptions] = useState([]);
  const [refreshingProfile, setRefreshingProfile] = useState(false);

  // Build menu options based on current page
  useEffect(() => {
    const close = () => dispatch(setShowMenu(false));
    const goSettings = (tab) => { dispatch(setSettingsTab(tab)); dispatch(setShowSettings(true)); close(); };
    const goProfile = () => { dispatch(setPage('profile')); close(); };
    const handleReloadChats = () => { reloadChats(dispatch, authUserId)(); close(); };
    const handleRefreshProfile = async () => {
      if (!profile?.id) return;
      setRefreshingProfile(true);
      dispatch(incrementOperation('profile'));
      dispatch(setProfileLoading(true));
      close();
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', profile.id)
          .maybeSingle();
        if (error) {
          /* Error handled */
        } else if (data) {
          dispatch(setProfile(data));
        }
      } catch (err) {
        /* Error handled */
      } finally {
        dispatch(decrementOperation('profile'));
        dispatch(setProfileLoading(false));
        setRefreshingProfile(false);
      }
    };
    const openShareCalendar = () => { dispatch(setShowShareCalendar(true)); close(); };
    const map = {
      match: [
        { label: 'Match Settings', icon: Settings, action: () => goSettings('match') },
        { label: 'Your Profile', icon: User, action: goProfile },
        { label: 'Calendar Sharing', icon: Share2, action: openShareCalendar },
      ],
      profile: [
        { label: 'Refresh Profile', icon: refreshingProfile || profileLoading ? Loader2 : RefreshCw, action: handleRefreshProfile, spinning: refreshingProfile || profileLoading },
        { label: 'Account Settings', icon: Settings, action: () => goSettings('account') },
        { label: 'Calendar Sharing', icon: Share2, action: openShareCalendar },
      ],
      calendar: [
        { label: 'Refresh Calendar', icon: RefreshCw, action: () => { dispatch({ type: 'calendar/refresh' }); close(); } },
        { label: 'Calendar Sharing', icon: Share2, action: openShareCalendar },
        { label: 'Settings', icon: Settings, action: () => goSettings('display') },
      ],
      store: [
        { label: 'Settings', icon: Settings, action: () => goSettings('account') },
        { label: 'Calendar Sharing', icon: Share2, action: openShareCalendar },
      ],
      chats: [
        { label: 'Reload Chats', icon: chatsLoading ? Loader2 : RefreshCw, action: handleReloadChats, spinning: chatsLoading },
        { label: 'Chat Settings', icon: Settings, action: () => goSettings('chat') },
        { label: 'Account Settings', icon: Settings, action: () => goSettings('account') },
        { label: 'Calendar Sharing', icon: Share2, action: openShareCalendar },
      ],
    };
    setOptions(map[page] ?? []);
  }, [page, dispatch, authUserId, chatsLoading, profile, profileLoading]);

  if (!open) return null;

  return (
    // Backdrop closes menu on click
    <div className="fixed inset-0 z-40" onClick={() => dispatch(setShowMenu(false))}>
      <div className="absolute inset-0 bg-foreground/30 backdrop-blur-sm" />
      {/* Dropdown panel — bg-card gives solid white background */}
      <div onClick={(e) => e.stopPropagation()} className="absolute right-3 top-16 w-64 overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="text-sm font-semibold text-foreground">Menu</span>
          <button onClick={() => dispatch(setShowMenu(false))}><X className="h-4 w-4 text-muted-foreground" /></button>
        </div>
        <ul className="py-1">
          {options.map((o) => (
            <li key={o.label}>
              <button onClick={o.action} className="flex w-full items-center gap-3 px-4 py-3 text-sm hover:bg-muted">
                <o.icon className={`h-4 w-4 text-primary ${o.spinning ? 'animate-spin' : ''}`} />
                <span className="flex-1 text-left text-foreground">{o.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default MenuDropdown;
